<template>
  <naj-box :title="local.lawsuitDetails.court">
    <arbitration-court-details
      v-if="isArbitrationLawsuit"
      :court-id.sync="caseData.courtId"
      :disabled="mode.isViewMode || auditFields.isCourtAudited"
      :handle-get-courts-invoker="invoker"
      :parties-info="casePartiesWithoutInheritors"
      :disable-location-specialization-rule="!applyLocationSpecializationRule"
      @courtsRetrieved="courtsRetrievedHandler"
      @change="courtChangedHandler"
    ></arbitration-court-details>

    <criminal-court-details
      v-else-if="isCriminalLawsuit(caseData) && isInspectionLawsuit(caseData)"
      :court-id.sync="caseData.courtId"
      :disabled="mode.isViewMode || isCourtFieldAuditedAndShouldBeDisabled"
      :handle-get-courts-invoker="invoker"
      :parties-info="casePartiesWithoutInheritors"
      :disable-location-specialization-rule="!applyLocationSpecializationRule"
      @courtsRetrieved="courtsRetrievedHandler"
      @change="courtChangedHandler"
    ></criminal-court-details>

    <commercial-court-details
      v-else-if="isCommercialLawsuit(caseData)"
      :court-id.sync="caseData.courtId"
      :disabled="mode.isViewMode || auditFields.isCourtAudited"
      :handle-get-courts-invoker="invoker"
      :parties-info="casePartiesWithoutInheritors"
      :disable-location-specialization-rule="false"
      @courtsRetrieved="courtsRetrievedHandler"
      @change="courtChangedHandler"
    ></commercial-court-details>
    <execution-court-details
      v-else-if="isExecutionLawsuit(caseData)"
      :court-id.sync="caseData.courtId"
      :disabled="mode.isViewMode || auditFields.isCourtAudited"
      :handle-get-courts-invoker="invoker"
      :parties-info="casePartiesWithoutInheritors"
      :disable-location-specialization-rule="!applyLocationSpecializationRule"
      @courtsRetrieved="courtsRetrievedHandler"
      @change="courtChangedHandler"
    ></execution-court-details>
    <personal-court-details
      v-else-if="isPersonalLawsuit(caseData)"
      :court-id.sync="caseData.courtId"
      :disabled="mode.isViewMode || auditFields.isCourtAudited"
      :handle-get-courts-invoker="invoker"
      :parties-info="casePartiesWithoutInheritors"
      :disable-location-specialization-rule="!applyLocationSpecializationRule"
      @courtsRetrieved="courtsRetrievedHandler"
      @change="courtChangedHandler"
    ></personal-court-details>
    <general-court-details
      v-else-if="isGeneralLawsuit(caseData)"
      :court-id.sync="caseData.courtId"
      :disabled="mode.isViewMode || auditFields.isCourtAudited"
      :handle-get-courts-invoker="invoker"
      :parties-info="casePartiesWithoutInheritors"
      :disable-location-specialization-rule="!applyLocationSpecializationRule"
      @courtsRetrieved="courtsRetrievedHandler"
      @change="courtChangedHandler"
    ></general-court-details>
    <labor-court-details
      v-else-if="isLaborLawsuit(caseData)"
      :court-id.sync="caseData.courtId"
      :disabled="mode.isViewMode || auditFields.isCourtAudited"
      :handle-get-courts-invoker="invoker"
      :parties-info="casePartiesWithoutInheritors"
      :disable-location-specialization-rule="!applyLocationSpecializationRule"
      @courtsRetrieved="courtsRetrievedHandler"
      @change="courtChangedHandler"
    ></labor-court-details>
    <default-court-details
      v-else
      :court-id.sync="caseData.courtId"
      :disabled="mode.isViewMode || auditFields.isCourtAudited"
      :handle-get-courts-invoker="invoker"
      :parties-info="casePartiesWithoutInheritors"
      :disable-location-specialization-rule="!applyLocationSpecializationRule"
      @courtsRetrieved="courtsRetrievedHandler"
      @change="courtChangedHandler"
    ></default-court-details>
    <v-col
      cols="12"
      lg="3"
      class="pt-9"
    >
      <naj-input
        v-if="showAuditFields"
        :type-id="formFieldType.checkbox"
        :value.sync="auditFields.isCourtAudited"
        :label="`${local.requestReply.checklist.validateCourt}`"
        class="inline-label primary-label-color"
        :disabled="isAuditFieldsDisabled"
        @change="updateAuditFields({ isCourtAudited: auditFields.isCourtAudited })"
      ></naj-input>
    </v-col>
  </naj-box>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import defaultCourtDetails from './default-court-details.vue';
import arbitrationCourtDetails from './arbitration-court-details.vue';
import executionCourtDetails from './execution-court-details.vue';
import generalCourtDetails from './general-court-details.vue';
import laborCourtDetails from './labor-court-details.vue';
import personalCourtDetails from './personal-court-details.vue';
import commercialCourtDetails from './commercial-court-details.vue';
import criminalCourtDetails from './criminal-court-details.vue';
import { countriesEnum, partyTypeEnum } from '@/helpers/enum';
import inspectionCourtDetails from './inspection-court-details.vue';

import {
  isLaborLawsuit,
  isPersonalLawsuit,
  isGeneralLawsuit,
  isCommercialLawsuit,
  isExecutionLawsuit,
  isInheritor,
  isLiquidatorOrJudicialGuard,
  isInspectionLawsuit,
  isCriminalLawsuit,
} from '../../../helpers/utils';

const { mapState, mapGetters, mapActions } = createNamespacedHelpers('LawsuitModule');

export default {
  inject: ['mode', '$validator'],
  components: {
    defaultCourtDetails,
    arbitrationCourtDetails,
    personalCourtDetails,
    generalCourtDetails,
    laborCourtDetails,
    executionCourtDetails,
    commercialCourtDetails,
    criminalCourtDetails,
  },
  props: {
    dynamicFormChanges: {
      type: Object,
      required: false,
      default: () => { },
    },
  },
  data() {
    return {
      isLaborLawsuit,
      isCommercialLawsuit,
      isExecutionLawsuit,
      isPersonalLawsuit,
      isGeneralLawsuit,
      isInspectionLawsuit,
      isCriminalLawsuit,
      invoker: null,
    };
  },
  computed: {
    ...mapState({
      caseData: state => state.lawsuitRequest.case,
      casePartiesWithoutInheritors(state) {
        return state.lawsuitRequest.caseParties
          .filter(cp => !isInheritor(cp) && !cp.relatedDeceasedSocialId && !isLiquidatorOrJudicialGuard(cp))
          .map((c) => {
            let casePartyRole = c.casePartyRole;
            const relatedParty = c.relatedPartiesInfo.find(rp => rp.relatedPartyType === partyTypeEnum.judicialGuard || rp.relatedPartyType === partyTypeEnum.liquidator);
            if (relatedParty) {
              casePartyRole = relatedParty.relatedCasePartyRole;
            }
            return {
              cityId: c.isPrisoner && c.prisonCityId ? c.prisonCityId : this.getAddress(c)?.cityId,
              casePartyRole: c.casePartyRole,
              isLocalAddress: this.isLocalAddress(c),
              gender: c.sex,
            };
          });
      },
      auditFields: state => state.lawsuitRequest.auditFields,
    }),
    ...mapGetters(['isAuditFieldsDisabled', 'selectedCaseType', 'showAuditFields']),
    isArbitrationLawsuit() {
      return (this.selectedCaseType || {}).canBeSentToAppealWithoutFI;
    },
    applyLocationSpecializationRule() {
      return (this.selectedCaseType || {}).applyLocationSpecializationRule;
    },
  },
  watch: {
    'caseData.caseTypeId'(value) {
      if (value) this.invoker = 'caseTypeId';
    },
    'caseData.caseCategoryId'(value) {
      if (value) this.invoker = 'caseCategoryId';
    },
    'caseData.caseClassificationIdNew'(value) {
      if (value) this.invoker = 'caseClassificationIdNew';
    },
    'caseData.forms'(newValue, oldValue) {
      if (!(oldValue || [])[0] && (newValue || [])[0])
        this.invoker = `forms-${Math.floor(Math.random() * 9999)}`;
    },
    casePartiesWithoutInheritors(value) {
      if (value) this.invoker = 'casePartiesWithoutInheritors';
    },
    isArbitrationLawsuit(value) {
      if (value) this.invoker = 'canBeSentToAppealWithoutFI';
    },
    dynamicFormChanges: {
      deep: true,
      handler() {
        this.invoker = `dynamicFormChanges-${Math.floor(Math.random() * 9999)}`;
      },
    },
  },
  methods: {
    ...mapActions(['updateAuditFields']),
    courtsRetrievedHandler(courts) {
      if (!courts.some(cc => cc.id === this.caseData.courtId)) {
        this.$set(this.caseData, 'courtId', null);
        if (this.auditFields.isCourtAudited)
          this.updateAuditFields({ isCourtAudited: false });
      }
    },
    courtChangedHandler(courtId) {
      this.$emit('change', courtId);
    },
    getAddress(p) {
      if (p.nationalAddress?.cityId)
        return p.nationalAddress;
      else if (p.placeAddress?.cityId)
        return p.placeAddress;
      return p.residenceAddress;
    },
    isLocalAddress(c) {
      if (c.nationalAddress?.cityId)
        return true;
      if (c.isPrisoner && c.prisonCityId)
        return true;
      return this.getAddress(c).countryId === countriesEnum.saudiArabia;
    },
  },
};
</script>
<style lang="scss" scoped>
.user-attention {
  font-size: 14px;
  color: red;
  font-weight: bold;
}
</style>
